import { useMemo, useState } from 'react';
import { Card, Empty } from '../components/ui';
import { CategoryBars, ChartFrame, StackedBars, StatTile } from '../components/Charts';
import type { Series } from '../components/Charts';
import { useResource } from '../state/hooks';
import { duration, percent, stat } from '../lib/format';
import type { ReportSummary } from '../lib/types';
import { useAuth } from '../state/AuthContext';

/**
 * Reporting.
 *
 * The scope shown here is decided by the server from the token — a call taker
 * sees their own work, a dispatcher the team's, an admin everything. The page
 * renders whatever came back rather than asking for a scope, so there is no
 * client-side switch that could ever widen it.
 */

const RANGES = [
  { id: '24h', label: 'Last 24 hours', hours: 24, bucket: 'hour' as const },
  { id: '7d', label: 'Last 7 days', hours: 24 * 7, bucket: 'day' as const },
  { id: '30d', label: 'Last 30 days', hours: 24 * 30, bucket: 'day' as const },
  { id: '90d', label: 'Last 90 days', hours: 24 * 90, bucket: 'week' as const },
];

export function Reports() {
  const { can } = useAuth();
  const [rangeId, setRangeId] = useState('7d');
  const range = RANGES.find((r) => r.id === rangeId) ?? RANGES[1];

  const path = useMemo(() => {
    const to = new Date();
    const from = new Date(to.getTime() - range.hours * 3600 * 1000);
    return `/reports/summary?from=${from.toISOString()}&to=${to.toISOString()}&bucket=${range.bucket}`;
  }, [range]);

  const report = useResource<ReportSummary>(path);
  const data = report.data;

  const series: Series[] = [
    { key: 'inbound', label: 'Inbound', color: 'var(--series-1)' },
    { key: 'outbound', label: 'Outbound', color: 'var(--series-2)' },
    { key: 'abandoned', label: 'Abandoned', color: 'var(--series-3)' },
  ];

  const volume = useMemo(() => {
    if (!data) return [];
    return data.callVolume.map((point) => {
      const at = new Date(point.bucket);
      return {
        label: at.toISOString(),
        fullLabel:
          range.bucket === 'hour'
            ? at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            : at.toLocaleDateString([], { day: '2-digit', month: 'short' }),
        // Abandoned calls are counted in `inbound` too, so the answered portion
        // is the difference — stacking them raw would double-count the column.
        values: [
          Math.max(0, point.inbound - point.abandoned),
          point.outbound,
          point.abandoned,
        ],
      };
    });
  }, [data, range.bucket]);

  if (report.error) return <Empty>{report.error}</Empty>;
  if (!data) return <Empty>Loading reports…</Empty>;

  const calls = data.calls;
  const abandonRate = calls && calls.calls > 0 ? percent(calls.abandoned, calls.calls) : '0%';
  const slaRate =
    data.sla && data.sla.with_sla > 0 ? percent(data.sla.with_sla - data.sla.breached, data.sla.with_sla) : '—';

  return (
    <div className="stack">
      <Card>
        <div className="row between">
          <div className="row tight">
            {RANGES.map((option) => (
              <button
                key={option.id}
                className={`chip${option.id === rangeId ? ' on' : ''}`}
                onClick={() => setRangeId(option.id)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <span className="faint">
            Scope: {data.scope === 'own' ? 'your own activity' : data.scope === 'team' ? 'your team' : 'whole organisation'}
          </span>
        </div>
      </Card>

      <div className="grid-tiles">
        <StatTile label="Calls" value={stat(calls?.calls ?? 0)} detail={`${calls?.abandoned ?? 0} abandoned`} />
        <StatTile
          label="Abandonment"
          value={abandonRate}
          detail="callers who hung up while waiting"
          tone={calls && calls.calls > 0 && calls.abandoned / calls.calls > 0.1 ? 'critical' : 'normal'}
        />
        <StatTile label="Average wait" value={duration(calls?.avg_wait_seconds ?? null)} detail={`longest ${duration(calls?.max_wait_seconds ?? null)}`} />
        <StatTile label="Average talk time" value={duration(calls?.avg_talk_seconds ?? null)} />
        <StatTile
          label="Time to dispatch"
          value={duration(data.timeToDispatch?.avg_seconds ?? null)}
          detail={`90th percentile ${duration(data.timeToDispatch?.p90_seconds ?? null)}`}
        />
        <StatTile
          label="Time to resolution"
          value={duration(data.timeToResolution?.avg_seconds ?? null)}
          detail={`${data.timeToResolution?.resolved ?? 0} resolved`}
        />
        <StatTile
          label="Dispatch SLA met"
          value={slaRate}
          detail={`${data.sla?.breached ?? 0} of ${data.sla?.with_sla ?? 0} breached`}
          tone={(data.sla?.breached ?? 0) > 0 ? 'critical' : 'normal'}
        />
      </div>

      <ChartFrame
        title="Call volume"
        subtitle={`Bucketed by ${range.bucket}. Inbound excludes abandoned, which is stacked separately.`}
        series={series}
        table={
          <div className="scroll-md">
            <table>
              <thead>
                <tr>
                  <th>Period</th>
                  <th className="num">Inbound answered</th>
                  <th className="num">Outbound</th>
                  <th className="num">Abandoned</th>
                </tr>
              </thead>
              <tbody>
                {volume.map((point) => (
                  <tr key={point.label}>
                    <td>{point.fullLabel}</td>
                    <td className="num">{point.values[0]}</td>
                    <td className="num">{point.values[1]}</td>
                    <td className="num">{point.values[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        }
      >
        <StackedBars data={volume} series={series} />
      </ChartFrame>

      <div className="columns two">
        <ChartFrame
          title="Incidents by type"
          subtitle="Red marks the share that missed their dispatch SLA."
          series={[]}
          table={
            <table>
              <thead>
                <tr><th>Type</th><th className="num">Total</th><th className="num">Breached</th></tr>
              </thead>
              <tbody>
                {data.categories.map((row) => (
                  <tr key={row.category}>
                    <td>{row.category}</td>
                    <td className="num">{row.total}</td>
                    <td className="num">{row.breached}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          }
        >
          <CategoryBars
            data={data.categories.map((row) => ({
              label: row.category,
              total: row.total,
              breached: row.breached,
            }))}
          />
        </ChartFrame>

        {data.scope === 'own' ? (
          <Card title="Team performance">
            <Empty>Your role reports on your own activity only.</Empty>
          </Card>
        ) : (
          <Card title="Per agent" flush>
            <div className="scroll-md">
              <table>
                <thead>
                  <tr>
                    <th>Agent</th>
                    <th className="num">Calls</th>
                    <th className="num">Avg talk</th>
                    <th className="num">Incidents</th>
                  </tr>
                </thead>
                <tbody>
                  {data.agents.map((agent) => (
                    <tr key={agent.id}>
                      <td>
                        {agent.full_name}
                        <div className="faint">{agent.role.replace('_', ' ')}</div>
                      </td>
                      <td className="num">{agent.calls_handled}</td>
                      <td className="num">{duration(agent.avg_talk_seconds)}</td>
                      <td className="num">{agent.incidents_created}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {data.stations.length > 0 && (
        <ChartFrame
          title="Incidents by police station"
          subtitle="Where incidents were sent. Red marks those that missed their dispatch SLA."
          series={[]}
          table={
            <table>
              <thead>
                <tr>
                  <th>Station</th><th>Zone</th>
                  <th className="num">Incidents</th><th className="num">Breached</th>
                  <th className="num">Avg time to station</th>
                </tr>
              </thead>
              <tbody>
                {data.stations.map((row) => (
                  <tr key={row.id}>
                    <td>{row.name}</td>
                    <td className="faint">{row.zone ?? '—'}</td>
                    <td className="num">{row.incidents}</td>
                    <td className="num">{row.breached}</td>
                    <td className="num">{duration(row.avg_seconds_to_station)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          }
        >
          <CategoryBars
            data={data.stations
              .filter((row) => row.incidents > 0)
              .map((row) => ({ label: row.name, total: row.incidents, breached: row.breached }))}
          />
        </ChartFrame>
      )}

      {can('reports.team') && data.units.length > 0 && (
        <Card title="Unit workload" flush>
          <div className="scroll-md">
            <table>
              <thead>
                <tr>
                  <th>Unit</th>
                  <th className="num">Assignments</th>
                  <th className="num">Avg time to scene</th>
                </tr>
              </thead>
              <tbody>
                {data.units.map((unit) => (
                  <tr key={unit.id}>
                    <td>
                      <strong>{unit.call_sign}</strong>
                      <div className="faint">{unit.name}</div>
                    </td>
                    <td className="num">{unit.assignments}</td>
                    <td className="num">{duration(unit.avg_response_seconds)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
